import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Chip, IconButton, Button, TextField, InputAdornment,
  Dialog, DialogTitle, DialogContent, DialogActions, FormControl, InputLabel,
  Select, MenuItem, CircularProgress, TablePagination, Tooltip, Stack
} from '@mui/material';
import {
  Search as SearchIcon,
  Clear as ClearIcon,
  Edit as EditIcon,
  PersonAdd as PersonAddIcon
} from '@mui/icons-material';
import apiClient from '../../services/apiClient';

// Role color mapping
const getRoleColor = (role) => {
  switch (role) {
    case 'admin':
      return 'error';
    case 'content_creator':
      return 'success';
    case 'editor':
      return 'info';
    default:
      return 'default';
  }
};

// Role options
const roleOptions = [
  { value: 'admin', label: 'Admin' },
  { value: 'content_creator', label: 'Content Creator' },
  { value: 'editor', label: 'Editor' },
  { value: 'user', label: 'Regular User' }
];

// Friendly display label for a role value (e.g. content_creator → Content Creator)
const roleLabel = (role) =>
  roleOptions.find((r) => r.value === role)?.label ||
  (role || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

const UserManagement = () => {
  // State
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedRole, setSelectedRole] = useState('');
  const [replaceExisting, setReplaceExisting] = useState(false);
  const [updatingRole, setUpdatingRole] = useState(false);

  // Load users on component mount
  useEffect(() => {
    fetchUsers();
  }, []);

  // Fetch users from API
  const fetchUsers = async () => {
    setLoading(true);
    try {
      const response = await apiClient.admin.getUsers();
      setUsers(response.data.users || []);
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setLoading(false);
    }
  };

  // Handle search input change
  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    setPage(0);
  };

  // Clear search
  const clearSearch = () => {
    setSearchTerm('');
  };

  // Open edit role dialog
  const handleOpenEditDialog = (user) => {
    setSelectedUser(user);
    // If user has roles, select the first one as default
    setSelectedRole(user.roles && user.roles.length > 0 ? user.roles[0].role : 'user');
    setReplaceExisting(false);
    setEditDialogOpen(true);
  };

  // Close edit role dialog
  const handleCloseEditDialog = () => {
    setEditDialogOpen(false);
    setSelectedUser(null);
    setSelectedRole('');
    setReplaceExisting(false);
  };

  // Handle role selection
  const handleRoleChange = (event) => {
    setSelectedRole(event.target.value);
  };

  // Handle replace existing toggle
  const handleReplaceChange = (event) => {
    setReplaceExisting(event.target.value === 'true');
  };

  // Update user role
  const handleUpdateRole = async () => {
    if (!selectedUser || !selectedRole) return;

    setUpdatingRole(true);

    try {
      await apiClient.admin.updateUserRole(selectedUser.user_id, {
        role: selectedRole,
        replace_existing: replaceExisting
      });

      // Update the user in the local state
      const updatedUsers = users.map(user => {
        if (user.user_id === selectedUser.user_id) {
          let updatedRoles;

          if (replaceExisting) {
            // Replace all roles with the new one
            updatedRoles = [{ role: selectedRole }];
          } else {
            // Add the new role if it doesn't exist already
            const hasRole = user.roles?.some(r => r.role === selectedRole);
            updatedRoles = hasRole ? [...user.roles] : [...(user.roles || []), { role: selectedRole }];
          }

          return { ...user, roles: updatedRoles };
        }
        return user;
      });

      setUsers(updatedUsers);
      handleCloseEditDialog();
    } catch (error) {
      console.error('Error updating user role:', error);
    } finally {
      setUpdatingRole(false);
    }
  };

  // Pagination
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Filter users based on search term
  const filteredUsers = users.filter(user => {
    const fullName = `${user.first_name} ${user.last_name}`.toLowerCase();
    return fullName.includes(searchTerm.toLowerCase()) ||
      user.email.toLowerCase().includes(searchTerm.toLowerCase());
  });

  // Pagination and display
  const displayedUsers = filteredUsers
    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  // Get user's role chips
  const getUserRoles = (user) => {
    const roles = user.roles || [];
    if (roles.length === 0) {
      return <Chip label="Regular User" size="small" variant="outlined" color="default" />;
    }

    return (
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
        {roles.map((role, index) => (
          <Chip
            key={index}
            label={roleLabel(role.role)}
            size="small"
            variant="outlined"
            color={getRoleColor(role.role)}
          />
        ))}
      </Box>
    );
  };

  return (
    <Box>
      {/* Header */}
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        alignItems={{ sm: 'center' }}
        justifyContent="space-between"
        sx={{ mb: 3, gap: 1 }}
      >
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 800 }}>User Management</Typography>
          <Typography variant="body2" color="text.secondary">
            Manage member accounts and assign admin roles.
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<PersonAddIcon />}
          onClick={() => console.log('This would open a user invitation form')}
          sx={{ textTransform: 'none', borderRadius: 2 }}
        >
          Invite User
        </Button>
      </Stack>

      {/* Filters and Search */}
      <Paper variant="outlined" sx={{ p: 2, mb: 3, borderRadius: 3 }}>
        <TextField
          fullWidth
          size="small"
          placeholder="Search by name or email…"
          value={searchTerm}
          onChange={handleSearchChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
            endAdornment: searchTerm && (
              <InputAdornment position="end">
                <IconButton size="small" onClick={clearSearch}>
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            )
          }}
        />
      </Paper>

      {/* Users Table */}
      <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 700 }}>Name</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Email</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Role</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Native Language</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Joined</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Last Login</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700 }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 5 }}>
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : displayedUsers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 5 }}>
                  <Typography variant="body2" color="text.secondary">
                    No users found
                  </Typography>
                  {searchTerm && (
                    <Button
                      sx={{ mt: 1, textTransform: 'none' }}
                      size="small"
                      onClick={clearSearch}
                    >
                      Clear search
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ) : (
              displayedUsers.map((user) => (
                <TableRow key={user.user_id} hover>
                  <TableCell component="th" scope="row" sx={{ fontWeight: 600 }}>
                    {user.first_name} {user.last_name}
                  </TableCell>
                  <TableCell>{user.email}</TableCell>
                  <TableCell>{getUserRoles(user)}</TableCell>
                  <TableCell>{user.native_language}</TableCell>
                  <TableCell>{formatDate(user.created_at)}</TableCell>
                  <TableCell>{formatDate(user.last_login)}</TableCell>
                  <TableCell align="right">
                    <Tooltip title="Edit Role">
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => handleOpenEditDialog(user)}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={filteredUsers.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </TableContainer>

      {/* Edit Role Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={handleCloseEditDialog}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Manage User Role</DialogTitle>
        <DialogContent>
          {selectedUser && (
            <Box sx={{ pt: 1 }}>
              <Typography variant="subtitle1" gutterBottom>
                {selectedUser.first_name} {selectedUser.last_name}
              </Typography>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                {selectedUser.email}
              </Typography>

              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Current Roles:
                </Typography>
                {selectedUser.roles && selectedUser.roles.length > 0 ? (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 2 }}>
                    {selectedUser.roles.map((role, index) => (
                      <Chip
                        key={index}
                        label={roleLabel(role.role)}
                        size="small"
                        variant="outlined"
                        color={getRoleColor(role.role)}
                      />
                    ))}
                  </Box>
                ) : (
                  <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                    Regular User (no special roles)
                  </Typography>
                )}
              </Box>

              <FormControl fullWidth sx={{ mt: 2 }}>
                <InputLabel id="role-label">Assign Role</InputLabel>
                <Select
                  labelId="role-label"
                  value={selectedRole}
                  label="Assign Role"
                  onChange={handleRoleChange}
                >
                  {roleOptions.map(role => (
                    <MenuItem key={role.value} value={role.value}>
                      {role.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth sx={{ mt: 2 }}>
                <InputLabel id="replace-label">Role Behavior</InputLabel>
                <Select
                  labelId="replace-label"
                  value={replaceExisting.toString()}
                  label="Role Behavior"
                  onChange={handleReplaceChange}
                >
                  <MenuItem value="false">Add to existing roles</MenuItem>
                  <MenuItem value="true">Replace existing roles</MenuItem>
                </Select>
              </FormControl>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCloseEditDialog} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button
            onClick={handleUpdateRole}
            variant="contained"
            color="primary"
            disabled={updatingRole}
            sx={{ textTransform: 'none', borderRadius: 2 }}
          >
            {updatingRole ? <CircularProgress size={24} /> : 'Update Role'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UserManagement;